import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../shared/auth";
import type { LiveRoomInfo } from "../../../shared/game-preview/LiveRoomPreview";
import { flagUrl } from "../../../shared/flags";
import { RANKED } from "../../../shared/rating/ranked";
import { RANKED_MODES, type RankedMode } from "../../../shared/rating/store";
import { getSettings, setSettings, subscribeSettings, type Settings } from "../../../shared/settings";
import { fullscreenSupported, toggleFullscreen, useFullscreen } from "../../../shared/fullscreen";
import { useOwnFullscreenButton } from "../../../app/FullscreenNudge";
import { inAndroidApp } from "../../../shared/app-shell";
import { buzz, canVibrate, testVibration, onAppBridge, VIBRATION_STRENGTHS } from "../../../shared/ui-feedback";
import PlayerScreen from "./PlayerScreen";
import { useLocalRoster, useOpenRooms, useRankedRatings, useRoyale, mmss, setReturnSheet, peekReturnSheet } from "./hooks";
import { LiveWindow, LiveFoot, RankButtons, RoomsBody, RoyaleInfo, RoyClock, RoySeats, ShopButton, rankView, ratingLine } from "./parts";
import { Bars, Crown, Doors, Full, FullExit, Gear, Grid, Help, Info, Music, News, Person, Play, RankBadge, Sound, Trophy, Vibrate } from "./icons";
import s from "./start.module.scss";
import Daily from "../../../shared/progress-ui/Daily";
import { AnnouncePill, PortalNotice } from "../../../shared/announce/Announce";
import { ON_PORTAL, FRAME_LOOK } from "../../../shared/portal-env";
import { useIsTouch } from "../../../shared/useResponsive";
import FrameHome from "./FrameHome";

type Sheet = null | "ranked" | "rooms" | "menu" | "settings" | "player" | "daily";
const SHEET_TITLE = { ranked: "Ranked", rooms: "Game Rooms", menu: "Menu", settings: "Settings", player: "Player", daily: "Your level" };

// Landscape thumb cluster: PLAY NOW's centre sits CX from the right edge and
// CY from the bottom. An arc button at angle a (deg, 180 = straight left, 90 =
// straight up) and distance r from that centre, size w x h.
const CX = 122, CY = 90;
const arcPos = (a: number, r: number, w: number, h = w): React.CSSProperties => {
  const rad = (a * Math.PI) / 180;
  return { right: CX - r * Math.cos(rad) - w / 2, bottom: CY + r * Math.sin(rad) - h / 2, width: w, height: h };
};

// The start page on a phone held sideways: a game app, not a website. The live
// Play Now game fills the screen; PLAY NOW is a big round button where the
// right thumb rests, with Royale, Ranked and Game Rooms in an arc around it.
// Menu, settings, Ranked, Game Rooms and Player are panels that slide in from
// the right. Play Now, Royale and Ranked lead to their own pages.
export default function StartLandscape({ onLogin, onLocal }: { onLogin: () => void; onLocal: () => void }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [info, setInfo] = useState<LiveRoomInfo | null>(null);
  const [sheet, setSheet] = useState<Sheet>(() => peekReturnSheet() as Sheet);
  // What the phone answered to Test vibration (settings sheet, the app only).
  const [vibeNote, setVibeNote] = useState<string | null>(null);
  // the Android app's haptics line opens a moment after the page: show its rows then
  const [, bridged] = useState(0);
  useEffect(() => onAppBridge(() => bridged((n) => n + 1)), []);
  // Coming back from a page to the screen it was opened from: that screen is
  // simply there again, it does not open over the home screen a second time.
  const [restored, setRestored] = useState<Sheet>(() => peekReturnSheet() as Sheet);
  useEffect(() => setReturnSheet(null), []);
  // leave for a page, coming back to this panel
  const goFrom = (path: string) => { setReturnSheet(sheet); navigate(path); };
  // Royale opens its card first; Join takes you in (the /royale page, where you
  // can play while you wait)
  const [royaleCard, setRoyaleCard] = useState(false);
  const royale = useRoyale();
  const lastWin = royale.count?.lastWinner;
  const rooms = useOpenRooms();
  const { ratings, signedIn } = useRankedRatings();
  const roster = useLocalRoster();
  const me = roster.players[0];
  const rating = ratingLine(ratings, signedIn);

  const [prefs, setPrefs] = useState<Settings>(getSettings);
  useEffect(() => subscribeSettings(setPrefs), []);
  const update = (patch: Partial<Settings>) => setSettings({ ...getSettings(), ...patch });
  const fullscreen = useFullscreen();
  const canFullscreen = fullscreenSupported();
  // the top row has its own full screen button, so no "Go full screen" pill here
  useOwnFullscreenButton(canFullscreen);

  // Signing in closes whatever panel asked for it.
  const login = () => { setSheet(null); onLogin(); };
  // a game site's frame on a computer (not a touch screen): its own start screen
  const touch = useIsTouch();
  const frameLook = ON_PORTAL && !touch ? FRAME_LOOK : null;
  const openRanked = (mode: RankedMode) => (signedIn ? navigate("/ranked/" + RANKED[mode].slug) : login());
  // the menu's tiles; Settings opens its panel, the rest go to their pages
  const go = (path: string) => () => goFrom(path);
  const tiles: [string, () => React.ReactElement, () => void][] = [
    ["Settings", Gear, () => setSheet("settings")], ["Leaderboard", Bars, go("/leaderboard")],
    ...(user ? [["Profile", Person, go("/profile")] as [string, () => React.ReactElement, () => void]] : []),
    ["News", News, go("/news")], ["About", Info, go("/about")], ["Help", Help, go("/help")],
  ];

  // (an open panel lifts the page over everything else; Player Settings does
  // not, so that the sign-in it can open shows over it)
  return (
    <div className={`${s.land} ${(sheet && sheet !== "player") || royaleCard ? s.sheetOpen : ""}`}>
      <div className={s.aurora} aria-hidden="true"><i className={s.blobTeal} /><i className={s.blobGreen} /><i className={s.blobViolet} /></div>
      <div className={s.grain} aria-hidden="true" />

      {/* a game site's frame on a computer: the start screen made for it (FrameHome) */}
      {frameLook ? (
        <FrameHome
          look={frameLook} me={me} royale={royale} info={info} onInfo={setInfo}
          onPlay={() => navigate("/play-now")} onRoyale={() => setRoyaleCard(true)} onRooms={() => setSheet("rooms")}
          onMenu={() => setSheet("menu")} onPlayer={() => setSheet("player")} onDaily={() => setSheet("daily")}
          onLeaderboard={go("/leaderboard")} notice={ON_PORTAL ? <PortalNotice /> : null}
        />
      ) : (<>
        <div className={s.lStage}><LiveWindow onInfo={setInfo} /></div>
        <div className={s.lShade} aria-hidden="true" />

        <div className={s.lHome}>
          {/* the profile chip opens player setup; signed out it has Sign in beside it */}
          <div className={s.lTop}>
            <button type="button" className={s.lProfile} onClick={() => setSheet("player")} aria-label="Player">
              <span className={s.lAvatar}><img src={flagUrl(me.flag)} alt="" /></span>
              <span className={s.lWho}><b>{me.name || "Guest"}</b></span>
            </button>
            {/* (a game site's build has no sign-in of ours: shared/portal-env.ts) */}
            {!signedIn && !ON_PORTAL && <button type="button" className={s.signIn} onClick={login}>Sign in</button>}
          </div>
          {/* the latest news or an announcement, top centre */}
          <div className={s.lAnnounce}>{ON_PORTAL ? <PortalNotice /> : <AnnouncePill />}</div>
          {/* the worm level and today's three, small: opens the Today panel */}
          <div className={s.lDaily}><Daily panel onOpen={() => setSheet("daily")} /></div>
          {/* who is in the live game, bottom left (no round or mode on a phone) */}
          <div className={s.lInfo}>
            <LiveFoot info={info} className={s.lFoot} lead={<span className={s.live}><i />LIVE</span>} />
          </div>
          <div className={s.lIcons}>
            {!ON_PORTAL && <ShopButton className={s.lShop} onClick={() => navigate("/shop")} />}
            {canFullscreen && (
              <button type="button" className={s.lIcon} onClick={toggleFullscreen} aria-pressed={fullscreen} aria-label={fullscreen ? "Leave full screen" : "Full screen"}>
                {fullscreen ? <FullExit /> : <Full />}
              </button>
            )}
            <button type="button" className={s.lIcon} onClick={() => setSheet("menu")} aria-label="Menu"><Grid /></button>
          </div>

          {/* the thumb cluster: PLAY NOW, and the fan of other modes up and to the left of it */}
          <div className={s.lCluster}>
            {/* (no Ranked on a game site's build: Rooms takes its place in the arc) */}
            <button type="button" className={s.lAct} style={ON_PORTAL ? arcPos(146, 150, 62) : arcPos(184, 140, 62)} onClick={() => setSheet("rooms")} aria-label="Game Rooms">
              <span className={s.lDisc}><Doors /></span>
              <span className={s.lLabel}>Rooms</span>
            </button>
            {/* Ranked: one capsule with both rank badges */}
            {!ON_PORTAL && (
              <button type="button" className={`${s.lAct} ${s.lRanked}`} style={arcPos(146, 158, 104, 62)} onClick={() => setSheet("ranked")} aria-label={signedIn ? "Ranked" : "Ranked: sign in to play ranked"}>
                <span className={s.lCap}>
                  {RANKED_MODES.map((m) => <RankBadge key={m} look={rankView(m, ratings, signedIn).look} className={s.lBadge} inner={21} />)}
                </span>
                <span className={s.lLabel}>Ranked</span>
              </button>
            )}
            <button type="button" className={`${s.lAct} ${s.lRoy}`} style={arcPos(92, 138, 74)} onClick={() => setRoyaleCard(true)} aria-label="Royale">
              <span className={s.lDisc}>
                <svg className={s.lRing} viewBox="0 0 84 84" aria-hidden="true">
                  <circle cx="42" cy="42" r="40" fill="none" stroke="rgba(255,210,63,0.16)" strokeWidth="3" />
                  {royale.pct > 0 && <circle cx="42" cy="42" r="40" fill="none" stroke="#ffd23f" strokeWidth="3" strokeLinecap="round" pathLength={100} strokeDasharray={`${royale.pct} 100`} />}
                </svg>
                <span className={s.lRoyIn}><b>{mmss(royale.secs)}</b></span>
              </span>
              {/* the crown sits on top the way the label hangs under */}
              <span className={s.lRoyTop} aria-hidden="true"><Crown /></span>
              <span className={s.lLabel}>Royale</span>
            </button>
            <button type="button" className={s.lPlay} onClick={() => navigate("/play-now")} aria-label="Play Now" data-sound="confirm">
              {/* (the website's PC button swears; a phone's does not: see StartPC) */}
              <b>LET'S<br />GO!</b>
            </button>
          </div>
        </div>
      </>)}

      {/* ---------- the Royale card (as on PC), with Join ---------- */}
      {royaleCard && (
        <div className={`${s.scrim} ${s.lCenter}`} onClick={() => setRoyaleCard(false)}>
          <div className={s.lRoyModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Royale">
            <button type="button" className={s.lRoyX} onClick={() => setRoyaleCard(false)} aria-label="Close">×</button>
            <div className={`${s.roy} ${s.royE}`}>
              <span className={s.royBlock}>
                <span className={s.royShine} />
                <Crown />
                {lastWin && lastWin.name ? <span className={s.royPlate}>{lastWin.flag ? <img src={flagUrl(lastWin.flag)} alt="" /> : null}<b>{lastWin.name}</b></span> : null}
              </span>
              <span className={s.royBody}>
                <span className={s.royRow}>
                  <span className={s.royName}>ROYALE</span>
                  <span className={s.royClockRow}><RoyaleInfo /><RoyClock secs={royale.secs} /></span>
                </span>
                <span className={s.royTag}>Up to <b>30 players</b><i />only one survives</span>
                <RoySeats pct={royale.pct} />
              </span>
            </div>
            <button type="button" className={s.lJoin} onClick={() => navigate("/royale")} data-sound="confirm"><Crown />Join Royale</button>
          </div>
        </div>
      )}

      {/* ---------- Player: a full screen of its own (PlayerScreen) ---------- */}
      {sheet === "player" && (
        <PlayerScreen
          roster={roster} signedIn={signedIn} instant={restored === "player"}
          onClose={() => { setRestored(null); setSheet(null); }}
          onProfile={() => goFrom("/profile")}
          onShop={ON_PORTAL ? undefined : (tab) => goFrom(tab ? "/shop?tab=" + tab : "/shop")}
          // (the sign-in opens over this screen: backing out of it leaves you here)
          onLogin={onLogin}
          onSignOut={() => { setSheet(null); void signOut(); }}
        />
      )}

      {/* ---------- panels (slide in from the right) ---------- */}
      {sheet && sheet !== "player" && (
        <div className={s.scrim} onClick={() => setSheet(null)}>
          <div className={s.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={SHEET_TITLE[sheet]}>
            <button type="button" className={s.close} onClick={() => setSheet(null)} aria-label="Close">×</button>
            <div className={s.boxHead}>
              {sheet === "ranked" ? <Trophy /> : sheet === "rooms" ? <Doors /> : sheet === "menu" ? <Grid /> : sheet === "daily" ? null : <Gear />}
              <h2>{SHEET_TITLE[sheet]}</h2>
            </div>
            {sheet === "menu" ? (<>
              {/* the same card as the player chip opens (Sign in keeps to itself) */}
              <div className={s.lMe} role="button" tabIndex={0} aria-label="Player" onClick={() => setSheet("player")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSheet("player"); } }}>
                <img src={flagUrl(me.flag)} alt="" />
                {/* (a game site's build has no accounts of ours: no "Not signed in" there) */}
                <div><b>{me.name || "Guest"}</b>{!ON_PORTAL && <small>{rating.text}</small>}</div>
                {!signedIn && !ON_PORTAL && <button type="button" className={s.signIn} onClick={(e) => { e.stopPropagation(); login(); }}>Sign in</button>}
              </div>
              <div className={s.lTiles}>
                {tiles.map(([label, Icon, open]) => (
                  <button key={label} type="button" className={s.lTile} onClick={open}><Icon />{label}</button>
                ))}
              </div>
            </>) : sheet === "settings" ? (
              <div className={s.lRows}>
                <button type="button" className={s.lRow} onClick={() => update({ muted: !prefs.muted })} aria-pressed={!prefs.muted} data-sound="toggle">
                  <Sound /><span>Sound</span><i className={`${s.lSwitch} ${!prefs.muted ? s.lOn : ""}`} />
                </button>
                <label className={s.lRow}>
                  <Music /><span>Music volume</span>
                  <input className={s.lRange} type="range" min={0} max={100} value={prefs.volume} disabled={prefs.muted} onChange={(e) => update({ volume: Number(e.target.value) })} aria-label="Music volume" />
                </label>
                {canVibrate() && (
                  <button type="button" className={s.lRow} onClick={() => update({ vibration: !prefs.vibration })} aria-pressed={prefs.vibration} data-sound="toggle">
                    <Vibrate /><span>Vibration</span><i className={`${s.lSwitch} ${prefs.vibration ? s.lOn : ""}`} />
                  </button>
                )}
                {canVibrate() && prefs.vibration && (
                  <div className={s.lRow}>
                    <Vibrate /><span>Strength</span>
                    <span className={s.lChips} data-haptic="none">
                      {VIBRATION_STRENGTHS.map((v) => (
                        <button key={v.id} type="button" data-sound="toggle" aria-pressed={prefs.vibrationStrength === v.id} className={prefs.vibrationStrength === v.id ? s.lChipOn : s.lChip} onClick={() => { update({ vibrationStrength: v.id }); buzz(false, v.id); }}>{v.label}</button>
                      ))}
                    </span>
                  </div>
                )}
                {inAndroidApp() && (
                  <button type="button" className={s.lRow} data-sound="none" data-haptic="none" onClick={() => setVibeNote(testVibration())}>
                    <Vibrate /><span>Test vibration{vibeNote && <small className={s.lNote}>{vibeNote}</small>}</span><b className={s.lTest}>Test</b>
                  </button>
                )}
                {fullscreenSupported() && (<>
                  <button type="button" className={s.lRow} onClick={() => update({ autoFullscreen: !prefs.autoFullscreen })} aria-pressed={prefs.autoFullscreen} data-sound="toggle">
                    <Play /><span>Full screen during matches</span><i className={`${s.lSwitch} ${prefs.autoFullscreen ? s.lOn : ""}`} />
                  </button>
                  <button type="button" className={s.lRow} onClick={toggleFullscreen} aria-pressed={fullscreen} data-sound="toggle">
                    <Full /><span>Full screen now</span><i className={`${s.lSwitch} ${fullscreen ? s.lOn : ""}`} />
                  </button>
                </>)}
              </div>
            ) : sheet === "daily" ? (
              <Daily sheet onLogin={login} />
            ) : sheet === "ranked" ? (
              <RankButtons ratings={ratings} signedIn={signedIn} onOpen={openRanked} signInRow onLogin={login} />
            ) : (
              <RoomsBody rooms={rooms} onLocal={onLocal} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
